import dayjs from 'dayjs';

import { useEffect, useMemo } from 'react';

/**
 * Formats a date string for display in charts.
 * @param {string} dateString - The date string (e.g., 'YYYYMMDD').
 * @returns {string} - Formatted date (e.g., 'Jul 26').
 */
export const formatChartDate = (dateString) => {
  return dayjs(dateString, 'YYYYMMDD').format('MMM D');
};

/**
 * Helper function to format large numbers (e.g., 1234567 becomes 1.2M).
 * @param {number} num - The number to format.
 * @returns {string} The formatted string.
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || Number.isNaN(num)) return 'N/A'; // Handle NaN
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

/**
 * Prepares raw report data rows for Recharts or list display.
 * Can aggregate data by summing or averaging metrics for a given dimension.
 * Handles specific dimension naming for display.
 * @param {Array<Object>} dataRows - The 'rows' array from the GA report response.
 * @param {string} dimensionName - The name of the dimension to use for the X-axis/category (e.g., 'date', 'unifiedPagePathScreen', 'eventName').
 * @param {string} metricName - The name of the metric to use for the Y-axis/value (e.g., 'screenPageViews', 'eventCount').
 * @param {string} [averageOfMetric=null] - Optional. If provided, `metricName` will be averaged by this metric.
 * Only applicable when `dimensionName` is 'date'.
 * @returns {Array<Object>} - Formatted data array for charts or lists.
 */
export const prepareChartData = (
  dataRows,
  dimensionName,
  metricName,
  averageOfMetric = null,
) => {
  if (!dataRows || dataRows.length === 0) return [];

  if (dimensionName === 'date') {
    const dailyData = {};
    dataRows.forEach((row) => {
      const { date } = row || {};
      if (!date) return;

      if (!dailyData[date]) {
        dailyData[date] = { date: formatChartDate(date) };
        dailyData[date][metricName] = 0;
        if (averageOfMetric) {
          dailyData[date][averageOfMetric] = 0;
        }
      }
      dailyData[date][metricName] += parseFloat(row[metricName] || 0);
      if (averageOfMetric) {
        dailyData[date][averageOfMetric] += parseFloat(
          row[averageOfMetric] || 0,
        );
      }
    });

    if (averageOfMetric) {
      Object.keys(dailyData).forEach((dateKey) => {
        if (dailyData[dateKey][averageOfMetric] > 0) {
          dailyData[dateKey][metricName] /= dailyData[dateKey][averageOfMetric];
        } else {
          dailyData[dateKey][metricName] = 0;
        }
        delete dailyData[dateKey][averageOfMetric];
      });
    }

    return Object.values(dailyData).sort((a, b) =>
      dayjs(a.date, 'MMM D').diff(dayjs(b.date, 'MMM D')),
    );
  }

  // For categorical data (e.g., top pages, countries, browsers, events)
  const aggregatedData = {};
  const aggregatedDataIndex = {};
  dataRows.forEach((row, index) => {
    let dimValue = '';
    if (dimensionName === 'unifiedPagePathScreen') {
      const pageTitle = row.pageTitle || '';
      const pathScreen = row.unifiedPagePathScreen || '';
      const screenClass = row.screenClass || '';

      if (pageTitle && pageTitle !== '(not set)') {
        dimValue = pageTitle;
        if (
          screenClass &&
          screenClass !== '(not set)' &&
          screenClass !== pageTitle
        ) {
          dimValue += ` (${screenClass})`;
        }
      } else if (pathScreen && pathScreen !== '(not set)') {
        dimValue = pathScreen;
        if (
          screenClass &&
          screenClass !== '(not set)' &&
          screenClass !== pathScreen
        ) {
          dimValue += ` (${screenClass})`;
        }
      } else if (screenClass && screenClass !== '(not set)') {
        dimValue = screenClass;
      } else {
        dimValue = '(not set)';
      }
    } else if (dimensionName === 'eventName') {
      dimValue = row.eventName || '';
    } else {
      dimValue = row[dimensionName] || '';
    }

    if (!dimValue || dimValue === '(not set)') return;

    const metValue = parseFloat(row[metricName] || 0);
    if (!aggregatedData[dimValue]) {
      aggregatedData[dimValue] = 0;
    }
    aggregatedData[dimValue] += metValue;
    aggregatedDataIndex[dimValue] = index;
  });

  return Object.keys(aggregatedData)
    .map((name, index) => ({
      name,
      value: aggregatedData[name],
      index: aggregatedDataIndex[name],
    }))
    .sort((a, b) => b.value - a.value);
};

/**
 * Prepares data for a multi-line chart, aggregating a specific metric over time
 * for multiple selected dimensions.
 * @param {Array<Object>} dataRows - The 'rows' array from the GA report response.
 * @param {string} timeDimension - The dimension representing time (e.g., 'date').
 * @param {string} valueMetric - The metric whose values will be plotted (e.g., 'screenPageViews', 'eventCount').
 * @param {string} filterDimension - The dimension used to filter and group lines (e.g., 'unifiedPagePathScreen', 'eventName').
 * @param {Array<Object>} selectedItems - An array of objects, each containing the 'name' (display label)
 * and 'originalDimensionValue' (the actual value from the dataRows for filtering) of selected items.
 * @returns {Array<Object>} Formatted data array suitable for Recharts LineChart with multiple lines.
 */
export const prepareMultiLineChartData = (
  dataRows,
  timeDimension,
  valueMetric,
  filterDimension,
  selectedItems,
) => {
  if (
    !dataRows ||
    dataRows.length === 0 ||
    !selectedItems ||
    selectedItems.length === 0
  ) {
    return [];
  }

  const chartDataMap = new Map();

  // Initialize chartDataMap with all dates and zero values for selected items
  const allDates = [
    ...new Set(dataRows.map((row) => row[timeDimension])),
  ].sort();
  allDates.forEach((date) => {
    const formattedDate = formatChartDate(date);
    const entry = { [timeDimension]: formattedDate };
    selectedItems.forEach((item) => {
      entry[item.name] = 0;
    });
    chartDataMap.set(formattedDate, entry);
  });

  dataRows.forEach((row) => {
    const date = row[timeDimension];
    const formattedDate = formatChartDate(date);
    const metricValue = parseFloat(row[valueMetric] || 0);

    let currentItemName = '';
    // Determine the display name for the current row's filter dimension
    if (filterDimension === 'unifiedPagePathScreen') {
      const pageTitle = row.pageTitle || '';
      const pathScreen = row.unifiedPagePathScreen || '';
      const screenClass = row.screenClass || '';

      if (pageTitle && pageTitle !== '(not set)') {
        currentItemName = pageTitle;
        if (
          screenClass &&
          screenClass !== '(not set)' &&
          screenClass !== pageTitle
        ) {
          currentItemName += ` (${screenClass})`;
        }
      } else if (pathScreen && pathScreen !== '(not set)') {
        currentItemName = pathScreen;
        if (
          screenClass &&
          screenClass !== '(not set)' &&
          screenClass !== pathScreen
        ) {
          currentItemName += ` (${screenClass})`;
        }
      } else if (screenClass && screenClass !== '(not set)') {
        currentItemName = screenClass;
      } else {
        currentItemName = '(not set)';
      }
    } else if (filterDimension === 'eventName') {
      currentItemName = row.eventName || '';
    } else {
      currentItemName = row[filterDimension] || '';
    }

    // Find if this current row's item is among the selected items
    const selectedItemMatch = selectedItems.find(
      (item) => item.name === currentItemName,
    );

    if (selectedItemMatch && chartDataMap.has(formattedDate)) {
      chartDataMap.get(formattedDate)[selectedItemMatch.name] += metricValue;
    }
  });

  // Convert map values to array and sort by date
  return Array.from(chartDataMap.values()).sort((a, b) =>
    dayjs(a[timeDimension], 'MMM D').diff(dayjs(b[timeDimension], 'MMM D')),
  );
};

/**
 * Prepares data for a single line representing the sum of a metric
 * across selected items over time.
 * @param {Array<Object>} dataRows - The 'rows' array from the GA report response.
 * @param {string} timeDimension - The dimension representing time (e.g., 'date').
 * @param {string} valueMetric - The metric whose values will be summed (e.g., 'screenPageViews', 'eventCount').
 * @param {string} filterDimension - The dimension used to identify items (e.g., 'unifiedPagePathScreen', 'eventName').
 * @param {Array<Object>} selectedItems - An array of objects, each containing the 'name' (display label)
 * and 'originalDimensionValue' (the actual value from the dataRows for filtering) of selected items.
 * @returns {Array<Object>} Formatted data array suitable for a single Recharts Line.
 */
export const prepareSumOfSelectedData = (
  dataRows,
  timeDimension,
  valueMetric,
  filterDimension,
  selectedItems,
) => {
  if (
    !dataRows ||
    dataRows.length === 0 ||
    !selectedItems ||
    selectedItems.length === 0
  ) {
    return [];
  }

  const sumDataMap = new Map(); // Map to hold summed data by date: { 'Jul 26': 150 }

  // Initialize sumDataMap with all dates and zero sum
  const allDates = [
    ...new Set(dataRows.map((row) => row[timeDimension])),
  ].sort();
  allDates.forEach((date) => {
    sumDataMap.set(formatChartDate(date), 0);
  });

  dataRows.forEach((row) => {
    const date = row[timeDimension];
    const formattedDate = formatChartDate(date);
    const metricValue = parseFloat(row[valueMetric] || 0);

    let currentItemName = '';
    // Determine the display name for the current row's filter dimension
    if (filterDimension === 'unifiedPagePathScreen') {
      const pageTitle = row.pageTitle || '';
      const pathScreen = row.unifiedPagePathScreen || '';
      const screenClass = row.screenClass || '';

      if (pageTitle && pageTitle !== '(not set)') {
        currentItemName = pageTitle;
        if (
          screenClass &&
          screenClass !== '(not set)' &&
          screenClass !== pageTitle
        ) {
          currentItemName += ` (${screenClass})`;
        }
      } else if (pathScreen && pathScreen !== '(not set)') {
        currentItemName = pathScreen;
        if (
          screenClass &&
          screenClass !== '(not set)' &&
          screenClass !== pathScreen
        ) {
          currentItemName += ` (${screenClass})`;
        }
      } else if (screenClass && screenClass !== '(not set)') {
        currentItemName = screenClass;
      } else {
        currentItemName = '(not set)';
      }
    } else if (filterDimension === 'eventName') {
      currentItemName = row.eventName || '';
    } else {
      currentItemName = row[filterDimension] || '';
    }

    // Check if this row's item is one of the selected items
    const isItemSelected = selectedItems.some(
      (item) => item.name === currentItemName,
    );

    if (isItemSelected && sumDataMap.has(formattedDate)) {
      sumDataMap.set(
        formattedDate,
        sumDataMap.get(formattedDate) + metricValue,
      );
    }
  });

  // Convert map values to array of { date: 'Jul 26', value: 150 } objects
  return Array.from(sumDataMap.entries())
    .map(([date, value]) => ({
      date: date,
      value: value,
    }))
    .sort((a, b) => dayjs(a.date, 'MMM D').diff(dayjs(b.date, 'MMM D')));
};

/**
 * Helper function to format seconds into a readable time string (e.g., "1h 5m 2s").
 * @param {number} seconds - The number of seconds.
 * @returns {string} The formatted time string.
 */
const formatSecondsToTime = (seconds) => {
  if (Number.isNaN(seconds) || seconds < 0) {
    return '0s';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (remainingSeconds > 0 || (hours === 0 && minutes === 0)) {
    parts.push(`${remainingSeconds}s`);
  }

  return parts.join(' ');
};

/**
 * Formats raw Google Analytics API data into a more usable structure,
 * including a total row and percentage values for metrics.
 *
 * @param {Array<Object>} rawData - The raw response array from the GA4 API.
 * @param {Object} [selectedDimension] - An optional default selected dimension to aggregate metrics data with unique dimension.
 * @param {Object} [selectedEvent] - An optional selected event related to eventName dimension and eventCount metric
 * to calculate the included metrics data in aggregated data, otherwise defaults to include all type of eventName.
 * @param {Array<Object>} [filters] - An optional array of filters to apply to the data.
 * @param {Array<string>} [viewEvents] - An optional array of event names to be considered as 'views' for CTR calculation..
 * @param {Array<string>} [clickEvents] - An optional array of event names to be considered as 'clicks' for CTR calculation.
 * @param {boolean} [withCTR] - An optional flag wether to perform ctr operation or not.
 * @returns {Object} The formatted report data.
 */
export const formatRawData = (props) => {
  const {
    rawData,
    selectedDimension,
    selectedEvent,
    filters,
    viewEvents: viewEventsProp,
    clickEvents: clickEventsProp,
    withCTR = true,
  } = props;
  let viewEvents = viewEventsProp || [];
  let clickEvents = clickEventsProp || [];
  let defaultClickEvents = null;
  let defaultViewEvents = null;

  if (
    !rawData ||
    (Array.isArray(rawData) ? !rawData.length : !rawData?.rawData)
  ) {
    return null;
  }

  const [data_] = Array.isArray(rawData) ? rawData : [rawData.rawData];
  const { rows: rowsData, ...data } = data_ || {};

  const dimensionHeaders = data.dimensionHeaders.map((header) => header.name);
  const metricHeaders = data.metricHeaders.map((header) => header.name);

  // 1. First, process raw data into a basic object-based rows array
  const rows = rowsData.map((row) => {
    const rowData = {};
    row.dimensionValues.forEach((dimValue, i) => {
      rowData[data.dimensionHeaders[i].name] = dimValue.value;
    });
    row.metricValues.forEach((metricValue, i) => {
      rowData[data.metricHeaders[i].name] = metricValue.value;
    });
    return rowData;
  });

  const hasEventData =
    dimensionHeaders.includes('eventName') &&
    metricHeaders.includes('eventCount');
  const allEventNames = hasEventData
    ? [...new Set(rows.map((row) => row.eventName))].sort()
    : null;

  // Automatically set default view events if the user didn't provide them
  if ((!viewEvents || viewEvents?.length === 0) && hasEventData) {
    viewEvents = ['page_view', 'screen_view'];
    defaultViewEvents = allEventNames
      .filter((it) => viewEvents.some((it2) => it2 === it))
      .sort();
    viewEvents = defaultViewEvents;
  } else {
    defaultViewEvents = allEventNames
      .filter((it) => viewEvents.some((it2) => it2 === it))
      .sort();
    viewEvents = defaultViewEvents;
  }

  // Automatically find click events if the user didn't provide them
  if (
    (!clickEvents || clickEvents?.length === 0) &&
    hasEventData &&
    allEventNames
  ) {
    clickEvents = allEventNames.filter((name) => name.includes('click'));
    defaultClickEvents = allEventNames
      .filter((name) => name.includes('click'))
      .sort();
  } else {
    defaultClickEvents = allEventNames
      .filter((it) => clickEvents.some((it2) => it2 === it))
      .sort();
    clickEvents = defaultClickEvents;
  }

  // 2. Apply the filters to the rows
  const filteredRows = rows.filter((row) => {
    // If no filters are provided, include all rows
    if (!filters || filters.length === 0) {
      return true;
    }

    // A row must match ALL filters to be included
    return filters.every((filter) => {
      const { name, operator, value } = filter;
      const rowValue = row[name];

      if (rowValue === undefined) {
        return false; // Property doesn't exist in the row, so it doesn't match
      }

      switch (operator) {
        case 'EQUALS':
          return rowValue === value;
        case 'NOT_EQUALS':
          return rowValue !== value;
        default:
          return false; // Unknown operator
      }
    });
  });

  // 3. Re-calculation of eventCount metric data based on selectedEvent. This operates on the numeric `filteredRows`.
  const processedRows =
    selectedEvent?.value && hasEventData
      ? filteredRows.map((row) => {
          try {
            const key = selectedEvent?.metricName || 'eventCount';
            if (
              row.eventName &&
              row.eventName.toLowerCase().trim() !==
                selectedEvent.value.toLowerCase().trim() &&
              key in row
            ) {
              const newRow = {
                ...row,
                [key]: '0',
              };
              return newRow;
            }
          } catch {
            return row;
          }
          return row;
        })
      : filteredRows;

  // 4. Aggregate data based on selectedDimension. This also operates on the numeric `processedRows`.
  const selectedDimName = selectedDimension?.name || selectedDimension;
  const aggregatedData = {};

  if (hasEventData && selectedDimName && withCTR) {
    processedRows.forEach((row) => {
      const dimensionValue = row[selectedDimName];
      // Create a combined key to preserve eventName for CTR calculation
      const uniqueKey = `${dimensionValue}::${row.eventName}`;

      if (!aggregatedData[uniqueKey]) {
        // Initialize the new aggregated row
        aggregatedData[uniqueKey] = {
          [selectedDimName]: dimensionValue,
          eventName: row.eventName,
        };
        metricHeaders.forEach((metricName) => {
          aggregatedData[uniqueKey][metricName] = 0;
        });
        dimensionHeaders.forEach((dimName) => {
          if (dimName !== selectedDimName && dimName !== 'eventName') {
            aggregatedData[uniqueKey][dimName] = '(mixed)';
          }
        });
      }
      // Sum the metrics for each combined key
      metricHeaders.forEach((metricName) => {
        aggregatedData[uniqueKey][metricName] += parseInt(row[metricName], 10);
      });
    });
  } else {
    // Fallback to aggregating only by the selectedDimension
    processedRows.forEach((row) => {
      const dimensionValue = row[selectedDimName];
      if (!aggregatedData[dimensionValue]) {
        // Initialize the new aggregated row
        aggregatedData[dimensionValue] = {
          [selectedDimName]: dimensionValue,
          eventName: row.eventName,
        };
        metricHeaders.forEach((metricName) => {
          aggregatedData[dimensionValue][metricName] = 0;
        });
        dimensionHeaders.forEach((dimName) => {
          if (dimName !== selectedDimName && dimName !== 'eventName') {
            aggregatedData[dimensionValue][dimName] = '(mixed)';
          }
        });
      }
      // Sum the metrics for each dimension value
      metricHeaders.forEach((metricName) => {
        aggregatedData[dimensionValue][metricName] += parseInt(
          row[metricName],
          10,
        );
      });
    });
  }

  // Convert the aggregated object back to an array
  const aggregatedRows = Object.values(aggregatedData);

  // 5. Recalculate totals and calculate CTR based on the now aggregated rows
  const totalRow = {};
  metricHeaders.forEach((headerName) => {
    totalRow[headerName] = 0;
  });

  // CTR calculation logic
  const ctrDataAvailable = hasEventData && selectedDimName && withCTR;
  const pageCtrMap = {};

  if (ctrDataAvailable) {
    aggregatedRows.forEach((row) => {
      const page = row[selectedDimName];
      if (!pageCtrMap[page]) {
        pageCtrMap[page] = { pageViews: 0, pageClicks: 0 };
      }
      // Check for both 'page_view' and 'screen_view' for CTR denominator
      if (viewEvents.includes(row.eventName)) {
        pageCtrMap[page].pageViews += parseInt(row.eventCount, 10);
      } else if (clickEvents.includes(row.eventName)) {
        pageCtrMap[page].pageClicks += parseInt(row.eventCount, 10);
      }
    });
  }

  let totalPageViews = 0;
  let totalClicks = 0;

  // Sum totals for all metrics and for overall CTR
  aggregatedRows.forEach((row) => {
    metricHeaders.forEach((headerName) => {
      totalRow[headerName] += parseInt(row[headerName], 10);
    });
    // Check for both 'page_view' and 'screen_view' for overall CTR denominator
    if (viewEvents.includes(row.eventName)) {
      totalPageViews += parseInt(row.eventCount, 10);
    } else if (clickEvents.includes(row.eventName)) {
      totalClicks += parseInt(row.eventCount, 10);
    }
  });

  // Calculate overall CTR
  const overallCTR =
    totalPageViews > 0 ? (totalClicks / totalPageViews) * 100 : 0;

  // 6. Remap aggregatedRows into formattedRows using the new totals
  const formattedRows = aggregatedRows.map((row) => {
    const rowData = {};

    // Map dimension values
    dimensionHeaders.forEach((dimName) => {
      rowData[dimName] = row[dimName];
    });

    // Map metric values with percentages based on new totals
    metricHeaders.forEach((metricName) => {
      const mainValue = parseInt(row[metricName], 10);
      const totalValue = totalRow[metricName];
      const percentage = totalValue > 0 ? (mainValue / totalValue) * 100 : 0;

      const valueType =
        data.metricHeaders.find((it) => it.name === metricName)?.type ||
        'TYPE_INTEGER';
      const processedMainValue =
        valueType === 'TYPE_SECONDS'
          ? formatSecondsToTime(mainValue)
          : mainValue.toString();

      rowData[metricName] = {
        type: valueType,
        raw: mainValue,
        main: processedMainValue,
        secondary: percentage.toFixed(2),
      };
    });

    // Add CTR data from the new CTR map
    if (ctrDataAvailable) {
      const page = row[selectedDimName];
      const views = pageCtrMap[page]?.pageViews || 0;
      const clicks = pageCtrMap[page]?.pageClicks || 0;
      const pageCTR = views > 0 ? (clicks / views) * 100 : 0;

      // Cap the CTR at 100%
      const cappedCTR = Math.min(100, pageCTR);

      rowData['ctr'] = {
        raw: cappedCTR,
        main: `${cappedCTR.toFixed(2)}%`,
        secondary: null,
      };
    } else {
      rowData['ctr'] = null;
    }

    return rowData;
  });

  const formattedMappedRows = rows.map((row) => {
    const rowData = {};

    // Map dimension values
    dimensionHeaders.forEach((dimName) => {
      rowData[dimName] = row[dimName];
    });

    // Map metric values with percentages based on new totals
    metricHeaders.forEach((metricName) => {
      const mainValue = parseInt(row[metricName], 10);
      const totalValue = totalRow[metricName];
      const percentage = totalValue > 0 ? (mainValue / totalValue) * 100 : 0;

      const valueType =
        data.metricHeaders.find((it) => it.name === metricName)?.type ||
        'TYPE_INTEGER';
      const processedMainValue =
        valueType === 'TYPE_SECONDS'
          ? formatSecondsToTime(mainValue)
          : mainValue.toString();

      rowData[metricName] = {
        type: valueType,
        raw: mainValue,
        main: processedMainValue,
        secondary: percentage.toFixed(2),
      };
    });

    // Add CTR data from the new CTR map
    if (ctrDataAvailable) {
      const page = row[selectedDimName];
      const views = pageCtrMap[page]?.pageViews || 0;
      const clicks = pageCtrMap[page]?.pageClicks || 0;
      const pageCTR = views > 0 ? (clicks / views) * 100 : 0;

      // Cap the CTR at 100%
      const cappedCTR = Math.min(100, pageCTR);

      rowData['ctr'] = {
        raw: cappedCTR,
        main: `${cappedCTR.toFixed(2)}%`,
        secondary: null,
      };
    } else {
      rowData['ctr'] = null;
    }

    return rowData;
  });

  // Finalize totalRow with calculated CTR
  if (ctrDataAvailable) {
    const cappedOverallCTR = Math.min(100, overallCTR);
    totalRow['ctr'] = {
      raw: cappedOverallCTR,
      main: `${cappedOverallCTR.toFixed(2)}%`,
      secondary: null,
    };
  } else {
    totalRow['ctr'] = null;
  }

  // Finalize totalRow for other metrics
  metricHeaders.forEach((headerName) => {
    const mainValue = totalRow[headerName];
    const valueType =
      data.metricHeaders.find((it) => it.name === headerName)?.type ||
      'TYPE_INTEGER';
    const processedMainValue =
      valueType === 'TYPE_SECONDS'
        ? formatSecondsToTime(mainValue)
        : mainValue.toString();

    totalRow[headerName] = {
      raw: mainValue,
      main: processedMainValue,
      secondary: 100,
    };
  });

  // 7. Return the full formatted object
  return {
    ...data,
    _dimensionHeaders: data.dimensionHeaders,
    _metricHeaders: data.metricHeaders,
    dimensionHeaders,
    metricHeaders,
    metricSelection: {
      // metric for eventName dimension is eventCount
      eventCount: allEventNames,
    },
    allEventNames,
    defaultViewEvents,
    defaultClickEvents,
    rows: processedRows,
    aggregatedRows,
    formattedRows,
    formattedMappedRows,
    totalRow,
    rawData,
    hasCTRData: ctrDataAvailable,
    ctrDataEligible: hasEventData && selectedDimName,
  };
};

export const descendingComparator = (a, b, orderBy) => {
  const aVal = a?.[orderBy]?.raw ?? a?.[orderBy] ?? null;
  const bVal = b?.[orderBy]?.raw ?? b?.[orderBy] ?? null;
  if (bVal < aVal) {
    return -1;
  }
  if (bVal > aVal) {
    return 1;
  }
  return 0;
};

export const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export const stableSort = (array = [], comparator = () => 0) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

export const areArraysIdentical = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;

  const countMap = {};

  for (let i = 0; i < arr1.length; i += 1) {
    const value = arr1[i];
    countMap[value] = (countMap[value] || 0) + 1;
  }

  for (let i = 0; i < arr2.length; i += 1) {
    const value = arr2[i];
    if (!countMap[value]) {
      return false;
    }
    countMap[value] -= 1;
  }

  const counts = Object.values(countMap);
  for (let i = 0; i < counts.length; i += 1) {
    if (counts[i] !== 0) {
      return false;
    }
  }

  return true;
};

export const useChartData = ({
  data,
  reportConfig,
  maxChartLines,
  selectedRowsForChart,
  onSelectedRowsForChart,
}) => {
  // Aggregate data for the table (top N items)
  const aggregatedTableData = useMemo(() => {
    return data
      ? prepareChartData(
          data.rows,
          reportConfig.timeSeriesDimension,
          reportConfig.timeSeriesMetric,
        )
      : [];
  }, [data, reportConfig]);

  // Effect to auto-select the first 5 items for the chart when data is fetched
  useEffect(() => {
    if (aggregatedTableData.length > 0) {
      if (selectedRowsForChart.length === 0) {
        const initialSelection = aggregatedTableData.slice(0, maxChartLines);
        onSelectedRowsForChart(initialSelection);
      }
    }
  }, [aggregatedTableData, maxChartLines, selectedRowsForChart]);

  // Prepare data for the multi-line chart based on selected rows
  const [
    chartData,
    totalDataForSelectedItems,
    // individualDataMap,
    // multiLineChartData,
  ] = useMemo(() => {
    const multiLineChartData_ = data
      ? prepareMultiLineChartData(
          data.rows,
          'date',
          reportConfig.timeSeriesMetric,
          reportConfig.timeSeriesDimension,
          selectedRowsForChart,
        )
      : [];
    const totalDataForSelectedItems_ = data
      ? prepareSumOfSelectedData(
          data.rows,
          'date',
          reportConfig.timeSeriesMetric,
          reportConfig.timeSeriesDimension,
          selectedRowsForChart,
        )
      : [];

    const individualDataMap_ = new Map();
    multiLineChartData_.forEach((dataPoint) => {
      individualDataMap_.set(dataPoint.date, dataPoint);
    });

    // Combine total data with individual selected item data
    const finalChartData_ = multiLineChartData_
      .map((dataPoint) => {
        const date = dataPoint.date;
        const totalValueEntry = totalDataForSelectedItems_.find(
          (item) => item.date === date,
        );
        return {
          // Add the total metric value for selected items under a distinct key
          [`Total ${reportConfig.timeSeriesMetric}`]: totalValueEntry
            ? totalValueEntry.value
            : 0,
          ...dataPoint,
        };
      })
      .sort((a, b) => dayjs(a.date, 'MMM D').diff(dayjs(b.date, 'MMM D')));

    // If no individual items are selected, but total data for selected items exists,
    // ensure the chart still shows the total line.
    // This scenario is mainly for when auto-selection kicks in or user deselects all but still wants total.
    if (
      selectedRowsForChart.length === 0 &&
      totalDataForSelectedItems_.length > 0 &&
      finalChartData_.length === 0
    ) {
      totalDataForSelectedItems_.forEach((item) => {
        finalChartData_.push({
          date: item.date,
          [`Total ${reportConfig.timeSeriesMetric}`]: item.value,
        });
      });
    }

    const newData = [
      finalChartData_,
      totalDataForSelectedItems_,
      individualDataMap_,
      multiLineChartData_,
    ];

    return newData;
  }, [data, reportConfig, selectedRowsForChart]);

  return {
    chartData,
    totalDataForSelectedItems,
    // individualDataMap,
    // multiLineChartData,
    // aggregatedTableData,
  };
};
